import {
  Box,
  Button,
  DateInput,
  Modal,
  TextInput,
  SelectInput,
  TextareaInput,
  toast,
} from '@palmetto/palmetto-components';
import { FormikValues, useFormik } from 'formik';
import { useEffect } from 'react';
import * as yup from 'yup';

import {
  IncentiveType,
  IncentiveTypeLabels,
  IncentiveStatus,
  IncentiveStatusLabels,
  IncentiveListItem,
  ProgramType,
  ProgramTypeLabels,
} from 'types';

import {
  useCreateIncentiveMutation,
  useUpdateIncentiveMutation,
  useLazyGetIncentiveQuery,
} from '../../services/incentives';

interface IncentiveModalProps {
  isOpen: boolean;
  onClose: () => void;
  incentive?: IncentiveListItem | null;
}

const incentiveTypeOptions = Object.entries(IncentiveTypeLabels).map(([value, label]) => ({
  value,
  label,
}));

const incentiveStatusOptions = Object.entries(IncentiveStatusLabels).map(([value, label]) => ({
  value,
  label,
}));

const programTypeOptions = Object.entries(ProgramTypeLabels).map(([value, label]) => ({
  value,
  label,
}));

const createValidationSchema = yup.object({
  name: yup.string().required('Incentive name is required'),
  type: yup.string().required('Type is required'),
  programTypes: yup.array(),
});

const editValidationSchema = yup.object({
  name: yup.string().required('Incentive name is required'),
  type: yup.string().required('Type is required'),
  programTypes: yup.array(),
  status: yup.string().required('Status is required'),
});

export const IncentiveModal = ({ isOpen, onClose, incentive }: IncentiveModalProps) => {
  const [createIncentive] = useCreateIncentiveMutation();
  const [updateIncentive] = useUpdateIncentiveMutation();
  const [fetchIncentive, { data: fullIncentive, isFetching }] = useLazyGetIncentiveQuery();

  const isEditMode = !!incentive;

  // Fetch full incentive details (including description) when editing
  useEffect(() => {
    if (isOpen && isEditMode && incentive?.key) {
      fetchIncentive({ key: incentive.key });
    }
  }, [isOpen, isEditMode, incentive?.key, fetchIncentive]);

  const formik = useFormik({
    initialValues: {
      name: incentive?.name || '',
      type: incentive?.type || IncentiveType.renewableEnergyCredit,
      description: fullIncentive?.description || '',
      programTypes: (incentive?.programTypes as string[]) || [],
      status: incentive?.status || IncentiveStatus.active,
      expirationDate: fullIncentive?.expirationDate ? new Date(fullIncentive.expirationDate) : null,
      salesforceId: fullIncentive?.salesforceId || '',
    },
    enableReinitialize: true,
    validationSchema: isEditMode ? editValidationSchema : createValidationSchema,
    onSubmit: async (values: FormikValues, { setSubmitting, resetForm }) => {
      try {
        if (isEditMode) {
          await updateIncentive({
            key: incentive.key,
            data: {
              name: values.name,
              type: values.type as IncentiveType,
              description: values.description || undefined,
              programTypes: values.programTypes as ProgramType[],
              status: values.status as IncentiveStatus,
              expirationDate: values.expirationDate || null,
              salesforceId: values.salesforceId || undefined,
            },
          }).unwrap();
          toast.success('Incentive updated successfully');
        } else {
          await createIncentive({
            name: values.name,
            type: values.type as IncentiveType,
            description: values.description || undefined,
            programTypes: values.programTypes as ProgramType[],
            status: IncentiveStatus.active,
            expirationDate: values.expirationDate || undefined,
            salesforceId: values.salesforceId || undefined,
          }).unwrap();
          toast.success('Incentive created successfully');
          resetForm();
        }
        onClose();
      } catch (error: any) {
        console.error(`Error ${isEditMode ? 'updating' : 'creating'} incentive:`, error);
        toast.error(error?.data?.message || `Error ${isEditMode ? 'updating' : 'creating'} incentive`);
      } finally {
        setSubmitting(false);
      }
    },
  });

  const handleClose = () => {
    formik.resetForm();
    onClose();
  };

  const modalTitle = isEditMode ? `Edit Incentive: ${incentive.name}` : 'Create New Incentive';
  const submitButtonText = isEditMode ? 'Save Changes' : 'Create Incentive';
  const isLoadingIncentive = isEditMode && isFetching;

  return (
    <Modal ariaLabelledBy="incentiveModal" isOpen={isOpen} onDismiss={handleClose} maxWidth="600px">
      <Modal.Header id="incentiveModal" title={modalTitle} onDismiss={handleClose} />
      <form onSubmit={formik.handleSubmit}>
        <Modal.Body background="secondary" overflow="auto" maxHeight="70vh">
          <Box direction="column" childGap="md">
            <TextInput
              type="text"
              label="Incentive Name"
              name="name"
              id="name"
              value={formik.values.name}
              onChange={(event) => formik.setFieldValue('name', event.target.value)}
              onBlur={formik.handleBlur}
              error={formik.touched.name && (formik.errors.name as string)}
              isRequired
              isDisabled={isLoadingIncentive}
            />

            <SelectInput
              label="Type"
              name="type"
              id="type"
              value={incentiveTypeOptions.find((opt) => opt.value === formik.values.type) || null}
              onChange={(event) => formik.setFieldValue('type', event.target.value?.value)}
              onBlur={formik.handleBlur}
              error={formik.touched.type && (formik.errors.type as string)}
              options={incentiveTypeOptions}
              isRequired
              isDisabled={isLoadingIncentive}
            />

            <TextareaInput
              label="Description"
              name="description"
              id="description"
              value={formik.values.description}
              onChange={(event) => formik.setFieldValue('description', event.target.value)}
              onBlur={formik.handleBlur}
              rows={3}
              isDisabled={isLoadingIncentive}
            />

            <SelectInput
              label="Program Types"
              name="programTypes"
              id="programTypes"
              value={formik.values.programTypes.map((pt: string) => ({
                value: pt,
                label: ProgramTypeLabels[pt as ProgramType],
              }))}
              onChange={(event) => {
                const values = Array.isArray(event.target.value) ? event.target.value.map((v: any) => v.value) : [];
                formik.setFieldValue('programTypes', values);
              }}
              onBlur={formik.handleBlur}
              error={formik.touched.programTypes && (formik.errors.programTypes as string)}
              options={programTypeOptions}
              isMulti
              helpText="Leave empty for global (all program types)"
              isDisabled={isLoadingIncentive}
            />

            <Box direction="row" childGap="sm" alignItems="flex-end">
              <Box flex="auto">
                <DateInput
                  id="expirationDate"
                  name="expirationDate"
                  label="Expiration Date"
                  placeholder="MM-DD-YYYY"
                  helpText="Optional date when this incentive expires"
                  value={
                    formik.values.expirationDate ? formik.values.expirationDate.toLocaleDateString('en-US') : ''
                  }
                  isDisabled={isLoadingIncentive}
                  datePickerProps={{
                    onChange: (date) => formik.setFieldValue('expirationDate', date),
                    selected: formik.values.expirationDate || undefined,
                  }}
                  textInputProps={{}}
                />
              </Box>
              {formik.values.expirationDate && (
                <Button
                  variant="secondary"
                  tone="neutral"
                  size="md"
                  iconPrefix="remove"
                  aria-label="Clear expiration date"
                  onClick={() => formik.setFieldValue('expirationDate', null)}
                  isDisabled={isLoadingIncentive}
                />
              )}
            </Box>

            <TextInput
              type="text"
              label="Salesforce ID"
              name="salesforceId"
              id="salesforceId"
              value={formik.values.salesforceId}
              onChange={(event) => formik.setFieldValue('salesforceId', event.target.value)}
              onBlur={formik.handleBlur}
              helpText="Optional Salesforce record ID"
              isDisabled={isLoadingIncentive}
            />

            {isEditMode && (
              <SelectInput
                label="Status"
                name="status"
                id="status"
                value={incentiveStatusOptions.find((opt) => opt.value === formik.values.status) || null}
                onChange={(event) => formik.setFieldValue('status', event.target.value?.value)}
                onBlur={formik.handleBlur}
                error={formik.touched.status && (formik.errors.status as string)}
                options={incentiveStatusOptions}
                isRequired
                isDisabled={isLoadingIncentive}
              />
            )}
          </Box>
        </Modal.Body>
        <Modal.Footer>
          <Button
            as="button"
            onClick={handleClose}
            variant="secondary"
            tone="neutral"
            size="md"
            isLoading={formik.isSubmitting}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            size="md"
            type="submit"
            isLoading={formik.isSubmitting}
            isDisabled={isLoadingIncentive}
          >
            {submitButtonText}
          </Button>
        </Modal.Footer>
      </form>
    </Modal>
  );
};
